import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return m
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday:'short', day:'numeric', month:'short', year:'numeric'
  })
}
function fmtTime(t) {
  if (!t) return '—'
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}

export default function EnrollExisting() {
  const [teachers,    setTeachers]    = useState([])
  const [students,    setStudents]    = useState([])
  const [selTeacher,  setSelTeacher]  = useState('')
  const [selStudent,  setSelStudent]  = useState('')
  const [whichCls,    setWhichCls]    = useState('future') // future | all
  const [teacherCls,  setTeacherCls]  = useState([])
  const [alreadyIn,   setAlreadyIn]   = useState(new Set()) // class ids student is already enrolled in
  const [selected,    setSelected]    = useState(new Set())
  const [loadingCls,  setLoadingCls]  = useState(false)
  const [busy,        setBusy]        = useState(false)
  const [done,        setDone]        = useState(null)
  const [err,         setErr]         = useState('')

  const today = new Date().toISOString().slice(0,10)
  const isMobile = useIsMobile()

  useEffect(() => { loadPeople() }, [])

  async function loadPeople() {
    const [{ data: t }, { data: s }] = await Promise.all([
      supabase.from('profiles').select('id,full_name').eq('role','teacher').order('full_name'),
      supabase.from('profiles').select('id,full_name,email').eq('role','student').order('full_name'),
    ])
    setTeachers(t || [])
    setStudents(s || [])
  }

  async function loadTeacherClasses(teacherId, studentId) {
    setTeacherCls([]); setSelected(new Set()); setDone(null); setErr('')
    if (!teacherId) return
    setLoadingCls(true)

    let q = supabase.from('classes').select('*').eq('teacher_id', teacherId).neq('is_cancelled', true)
    if (whichCls === 'future') q = q.gte('class_date', today)
    q = q.order('class_date').order('start_time')

    const { data: cls } = await q
    setTeacherCls(cls || [])

    if (studentId && cls?.length) {
      const { data: enrols } = await supabase.from('enrollments').select('class_id')
        .eq('student_id', studentId).in('class_id', cls.map(c => c.id))
      setAlreadyIn(new Set((enrols || []).map(e => e.class_id)))
    } else {
      setAlreadyIn(new Set())
    }
    setLoadingCls(false)
  }

  useEffect(() => {
    if (selTeacher) loadTeacherClasses(selTeacher, selStudent)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selTeacher, selStudent, whichCls])

  const enrollable = teacherCls.filter(c => !alreadyIn.has(c.id))

  function toggleOne(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  function selectAll()  { setSelected(new Set(enrollable.map(c => c.id))) }
  function selectNone() { setSelected(new Set()) }

  async function enrollSelected() {
    if (!selStudent) return setErr('Select a student first.')
    if (!selected.size) return setErr('Select at least one class.')
    setBusy(true); setErr('')

    try {
      const CHUNK = 50
      const ids = [...selected]
      for (let i = 0; i < ids.length; i += CHUNK) {
        const { error } = await supabase.from('enrollments').insert(
          ids.slice(i, i + CHUNK).map(class_id => ({ student_id: selStudent, class_id }))
        )
        if (error) throw new Error(error.message)
      }
      setDone({ count: ids.length })
      setSelected(new Set())
      await loadTeacherClasses(selTeacher, selStudent)
    } catch (e) {
      setErr('Enroll failed: ' + e.message)
    }
    setBusy(false)
  }

  const teacher = teachers.find(t => t.id === selTeacher)
  const student = students.find(s => s.id === selStudent)

  const inp = {
    width:'100%', background:'rgba(var(--fg-rgb),0.06)',
    border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px',
    padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))',
    outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box',
  }
  const lbl = {
    display:'block', fontSize:'10px', fontWeight:700,
    color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em',
    textTransform:'uppercase', marginBottom:'5px', marginTop:'12px',
  }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>🔗 Enroll Student in Existing Classes</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'20px' }}>
        For classes that were created without a student attached (e.g. via bulk scheduling) — pick a teacher, a student, and which of that teacher's classes to enroll them into.
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap:'16px' }}>
        {/* ── LEFT: pickers ── */}
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'16px', height:'fit-content' }}>
          <label style={{ ...lbl, marginTop:0 }}>Teacher *</label>
          <select style={{ ...inp, colorScheme:'dark' }} value={selTeacher} onChange={e => setSelTeacher(e.target.value)}>
            <option value="">— Choose teacher —</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>

          <label style={lbl}>Student *</label>
          <select style={{ ...inp, colorScheme:'dark' }} value={selStudent} onChange={e => setSelStudent(e.target.value)}>
            <option value="">— Choose student —</option>
            {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>

          <label style={lbl}>Which Classes</label>
          <div style={{ display:'grid', gap:'6px' }}>
            {[
              { v:'future', label:'Future classes only' },
              { v:'all',    label:'All classes (past + future)' },
            ].map(o => (
              <button key={o.v} type="button" onClick={() => setWhichCls(o.v)}
                style={{ padding:'9px 12px', borderRadius:'10px', border:`1px solid ${whichCls===o.v?'#1e90ff':'rgba(var(--fg-rgb),0.07)'}`, background:whichCls===o.v?'rgba(30,144,255,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', fontSize:'12px', fontWeight:700, color:whichCls===o.v?'#fff':'rgba(var(--fg-rgb),var(--a60))' }}>
                {o.label}
              </button>
            ))}
          </div>

          {teacher && student && (
            <div style={{ marginTop:'14px', padding:'12px', background:'rgba(30,144,255,0.08)', borderRadius:'10px', border:'0.5px solid rgba(30,144,255,0.2)', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a60))' }}>
              <div><strong style={{ color:'var(--fg-solid)' }}>{student.full_name}</strong> → <strong style={{ color:'var(--fg-solid)' }}>{teacher.full_name}</strong></div>
              <div style={{ marginTop:'4px', color:'rgba(var(--fg-rgb),var(--a35))' }}>
                {teacherCls.length} classes · {alreadyIn.size} already enrolled · {enrollable.length} available
              </div>
            </div>
          )}

          {done && (
            <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.2)', borderRadius:'8px', fontSize:'12px', color:'#34d399' }}>
              ✓ Enrolled into {done.count} class{done.count!==1?'es':''}
            </div>
          )}
        </div>

        {/* ── RIGHT: class list ── */}
        <div>
          {!selTeacher && (
            <div style={{ textAlign:'center', padding:'4rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', border:'0.5px solid rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>
              ← Select a teacher to see their classes
            </div>
          )}

          {selTeacher && loadingCls && (
            <div style={{ textAlign:'center', padding:'4rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading classes...</div>
          )}

          {selTeacher && !loadingCls && teacherCls.length === 0 && (
            <div style={{ textAlign:'center', padding:'3rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', border:'0.5px solid rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a25))', fontSize:'13px' }}>
              No classes found for this teacher.
            </div>
          )}

          {selTeacher && !loadingCls && teacherCls.length > 0 && (
            <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'16px' }}>
              <div style={{ display:'flex', gap:'6px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginRight:'4px' }}>Select:</span>
                <button onClick={selectAll} disabled={!selStudent} style={{ fontSize:'11px', fontWeight:600, padding:'4px 10px', borderRadius:'7px', border:'none', cursor:selStudent?'pointer':'not-allowed', fontFamily:'inherit', background:'rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a60))', opacity:selStudent?1:0.5 }}>All available</button>
                <button onClick={selectNone} style={{ fontSize:'11px', fontWeight:600, padding:'4px 10px', borderRadius:'7px', border:'none', cursor:'pointer', fontFamily:'inherit', background:'rgba(var(--fg-rgb),0.07)', color:'rgba(var(--fg-rgb),var(--a60))' }}>None</button>
                <span style={{ marginLeft:'auto', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{selected.size} of {enrollable.length} selected</span>
              </div>

              {!selStudent && (
                <div style={{ padding:'10px 12px', background:'rgba(244,163,53,0.08)', border:'0.5px solid rgba(244,163,53,0.2)', borderRadius:'9px', fontSize:'12px', color:'#f4a335', marginBottom:'10px' }}>
                  Select a student to enable enrolling.
                </div>
              )}

              <div style={{ maxHeight:'420px', overflowY:'auto', display:'grid', gap:'4px', marginBottom:'12px' }}>
                {teacherCls.map(cls => {
                  const isEnrolled = alreadyIn.has(cls.id)
                  const isChecked  = selected.has(cls.id)
                  return (
                    <div key={cls.id} onClick={() => !isEnrolled && selStudent && toggleOne(cls.id)}
                      style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'9px', cursor: isEnrolled || !selStudent ? 'default' : 'pointer', border:`1px solid ${isChecked?'rgba(16,185,129,0.4)':'rgba(var(--fg-rgb),0.06)'}`, background:isChecked?'rgba(16,185,129,0.08)':'rgba(var(--fg-rgb),0.02)', opacity: isEnrolled ? 0.5 : 1 }}>
                      <div style={{ width:'16px', height:'16px', borderRadius:'4px', border:`2px solid ${isChecked?'#10b981':'rgba(var(--fg-rgb),var(--a20))'}`, background:isChecked?'#10b981':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {isChecked && <span style={{ color:'var(--fg-solid)', fontSize:'10px', fontWeight:800 }}>✓</span>}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cls.title}</div>
                        <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'1px' }}>{fmtDate(cls.class_date)}{cls.start_time && ` · ${fmtTime(cls.start_time)}`}</div>
                      </div>
                      {isEnrolled && <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'5px', background:'rgba(16,185,129,0.15)', color:'#34d399', flexShrink:0 }}>Already enrolled</span>}
                    </div>
                  )
                })}
              </div>

              {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px', marginBottom:'10px' }}>{err}</div>}

              <button onClick={enrollSelected} disabled={busy || !selected.size || !selStudent}
                style={{ width:'100%', padding:'12px', background: (!selected.size || !selStudent) ? 'rgba(100,100,100,0.2)' : 'linear-gradient(135deg,#10b981,#059669)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor: (!selected.size || !selStudent || busy) ? 'not-allowed' : 'pointer', fontFamily:'inherit', opacity: (!selected.size || !selStudent) ? 0.5 : 1 }}>
                {busy ? '⏳ Enrolling...' : `🔗 Enroll into ${selected.size} Class${selected.size!==1?'es':''}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
